import './order-history.scss';

import { Modal, Tabs } from 'antd';
import { useMemo, useState } from 'react';

import { Button, Table } from '@/components/Shared/ui';

import useAutoCreditHistory from './hooks/useAutoCreditHistory';
import useIeLeasingHistory from './hooks/useIeLeasingHistory';
import useOnlineAutoCreditHistory from './hooks/useOnlineAutoCreditHistory';
import usePosCreditHistory from './hooks/usePosCreditHistory';
import {
  type HistoryDataSourceInterface,
  historyTableDataSource,
  onlineHistoryTableDataSource
} from './table-data-source';

interface IProps {
  historyType: 'auto-credit' | 'pos-credit' | 'ie-leasing';
  isOpened: boolean;
  onClose: (isOpened: boolean) => void;
  showOnlineAutoCreditHistory?: boolean;
}

const OrderHistory: React.FC<IProps> = ({
  historyType,
  isOpened,
  onClose,
  showOnlineAutoCreditHistory = false
}) => {
  const [tab, setTab] = useState('history');
  const [currentPage, setCurrentPage] = useState(1);

  const { autoCreditData, totalAutoCreditData, autoCreditLoading } =
    useAutoCreditHistory({
      shouldRequest: historyType === 'auto-credit' && isOpened
    });

  const { onlineAutoCreditData, onlineAutoCreditLoading } =
    useOnlineAutoCreditHistory({
      shouldRequest:
        historyType === 'auto-credit' && isOpened && showOnlineAutoCreditHistory
    });

  const { posCreditData, totalPosCreditData } = usePosCreditHistory();

  const { ieLeasingData, totalIeLeasingData } = useIeLeasingHistory();

  const state: Record<
    typeof historyType,
    { data?: HistoryDataSourceInterface[]; loading: boolean; total?: number }
  > = useMemo(() => {
    return {
      'auto-credit': {
        data: autoCreditData,
        loading: autoCreditLoading,
        total: totalAutoCreditData
      },
      'pos-credit': {
        data: posCreditData,
        loading: false,
        total: totalPosCreditData
      },
      'ie-leasing': {
        data: ieLeasingData,
        loading: false,
        total: totalIeLeasingData
      }
    };
  }, [
    autoCreditData,
    autoCreditLoading,
    posCreditData,
    totalAutoCreditData,
    totalPosCreditData,
    ieLeasingData,
    totalIeLeasingData
  ]);

  const handleClose = () => {
    onClose(false);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const mainHistoryTable = useMemo(() => {
    return (
      <div className="order-history__table">
        <Table
          key="history"
          dataSource={state[historyType].data}
          columns={historyTableDataSource}
          rowKey={(record) => record.id}
          pagination={{
            showSizeChanger: false,
            total: state[historyType].total,
            defaultPageSize: 10,
            current: currentPage,
            onChange: handlePageChange
          }}
          loading={state[historyType].loading}
        />
      </div>
    );
  }, [state, historyType, currentPage]);

  return (
    <Modal
      centered
      mask={{ closable: true }}
      className="order-history"
      title={!showOnlineAutoCreditHistory ? 'История изменений' : null}
      open={isOpened}
      onCancel={handleClose}
      footer={null}
      width={1000}
    >
      {showOnlineAutoCreditHistory ? (
        <Tabs
          activeKey={tab}
          onChange={setTab}
          items={[
            {
              label: 'История изменений',
              key: 'history',
              children: mainHistoryTable
            },
            {
              label: 'История заявки',
              key: 'online-history',
              children: (
                <div className="order-history__table">
                  <Table
                    dataSource={onlineAutoCreditData}
                    columns={onlineHistoryTableDataSource}
                    rowKey={(record) => record.uuid}
                    pagination={{
                      showSizeChanger: false,
                      defaultPageSize: 10
                    }}
                    loading={onlineAutoCreditLoading}
                  />
                </div>
              )
            }
          ]}
        />
      ) : (
        mainHistoryTable
      )}

      <div className="order-history__actions">
        <Button onClick={handleClose}>Закрыть</Button>
      </div>
    </Modal>
  );
};

export default OrderHistory;
